import styled from 'styled-components'
import DndComponent from '../../components/kanban/DndComponent'
import { useCallback, useMemo, useState } from 'react'
import {
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core'
import Card from '../../components/kanban/items/Card'
import {
  CardContent,
  CardData,
  Column,
  EditableCardItem,
  StatusColumnCumulationData,
  Swimlane,
  SwimlaneCumulationData,
  SwimlaneStatusCumulationMap,
  createDroppableId,
  parseDroppableId,
} from './Kanban'
import { WbsStatusCardContent } from './components/Card/WbsStatusCardContent'
import { StatusColumnHeader } from './components/Column/StatusColumnHeader'
import DroppableWrapper from '../../components/kanban/items/DroppableWrapper'
import { arrayMove } from '@dnd-kit/sortable'
import KanbanColumn, { COLLAPSED_COLUMN_WIDTH } from './components/Column'
import { SwimlaneRow } from './components/Swimlane'
import { colorPalette } from '../../style/colorPalette'
import { useColumnCollapse } from './hooks/useColumnCollapse'
import { StatusKanbanSearchCondition } from './hooks/useSearchCondition'

type Props = {
  columns: Column[]
  cards: { [id: string]: CardData }
  filter: (card: CardData) => boolean
  handleChangeCardContent: (
    id: string,
    field: EditableCardItem,
    changed: any
  ) => void
  setColumns: React.Dispatch<React.SetStateAction<Column[]>>
  refreshSingle: (
    wbsItemCode: string,
    condition?: StatusKanbanSearchCondition
  ) => void
  searchCondition: StatusKanbanSearchCondition
  swimlanes: Swimlane[]
  swimlaneCumulation: Record<string, SwimlaneCumulationData>
  swimlaneStatusCumulation: SwimlaneStatusCumulationMap
  swimlaneCollapseState: Record<string, boolean>
  onToggleSwimlaneCollapse: (id: string) => void
  registerSwimlaneRowRef: (id: string, ref: HTMLDivElement | null) => void
  setScrollContainerRef: (ref: HTMLDivElement | null) => void
  isSwimlaneEnabled: boolean
}

export const ColumnContainer = ({
  columns,
  cards,
  filter,
  handleChangeCardContent,
  setColumns,
  refreshSingle,
  searchCondition,
  swimlanes,
  swimlaneCumulation,
  swimlaneStatusCumulation,
  swimlaneCollapseState,
  onToggleSwimlaneCollapse,
  registerSwimlaneRowRef,
  setScrollContainerRef,
  isSwimlaneEnabled,
}: Props) => {
  const [overlayActiveContent, setActiveContent] = useState<
    CardContent | undefined
  >(undefined)

  const {
    collapseState: columnCollapseState,
    toggleCollapse: toggleColumnCollapse,
  } = useColumnCollapse()

  // ドラッグ中の担当者ID（他のスイムレーンを薄くするために使用）
  const draggingResponsibleId = overlayActiveContent?.responsible?.uuid ?? null
  const isDragging = overlayActiveContent !== undefined

  // フィルタ済み集計データを計算
  const filteredCumulationByColumn = useMemo(() => {
    // フィルタ後の全体件数を先に計算
    const filteredTotalCount = Object.values(cards).filter(
      card => card && filter(card)
    ).length

    return columns.reduce<Record<string, StatusColumnCumulationData>>(
      (acc, column) => {
        const filteredCards = column.cardIds
          .map(id => cards[id])
          .filter(card => card && filter(card))

        acc[column.id] = {
          count: filteredCards.length,
          totalCount: filteredTotalCount,
          estimatedHour: filteredCards.reduce(
            (sum, card) => sum + (card.content.estimatedWorkload?.hour ?? 0),
            0
          ),
          actualHour: filteredCards.reduce(
            (sum, card) => sum + (card.content.actualHour ?? 0),
            0
          ),
        }
        return acc
      },
      {}
    )
  }, [columns, cards, filter])

  // カードから担当者IDを取得
  const getResponsibleIdFromCard = useCallback(
    (cardId: string): string | null => {
      return cards[cardId]?.content.responsible?.uuid ?? null
    },
    [cards]
  )

  // over IDから担当者IDを取得（DroppableIdまたはcardId）
  const getResponsibleIdFromOverId = useCallback(
    (overId: string): string | null => {
      const parsed = parseDroppableId(overId)
      if (parsed) return parsed.responsibleId
      return getResponsibleIdFromCard(overId)
    },
    [getResponsibleIdFromCard]
  )

  // DroppableIdまたはcardIdからカラムを検索
  const findColumnFromId = useCallback(
    (
      id: string | null
    ): {
      column: Column | null
      status: string | null
    } => {
      if (!id) return { column: null, status: null }

      // DroppableIdの場合（format: responsibleUuid_status）
      const parsed = parseDroppableId(id)
      if (parsed) {
        const column = columns.find(c => c.id === parsed.status) ?? null
        return { column, status: parsed.status }
      }

      // スイムレーンオフ時: IDがそのままカラムIDの場合
      const directColumn = columns.find(c => c.id === id)
      if (directColumn) {
        return { column: directColumn, status: directColumn.id }
      }

      // カードIDの場合 - カードが現在どのカラムに属しているかを探す
      // (handleDragOverで移動している可能性があるため、cardIdsから検索)
      for (const col of columns) {
        if (col.cardIds.includes(id)) {
          return { column: col, status: col.id }
        }
      }

      // cardIdsに見つからない場合、カードの元のステータスを使用
      const card = cards[id]
      if (card) {
        const column = columns.find(c => c.id === card.content.status) ?? null
        return { column, status: card.content.status ?? null }
      }

      return { column: null, status: null }
    },
    [columns, cards]
  )

  // 特定のスイムレーンセルのカードを取得（column.cardIdsの順序を維持）
  const getCardsForCell = useCallback(
    (responsibleUuid: string | null, statusId: string): CardData[] => {
      const column = columns.find(c => c.id === statusId)
      if (!column) return []

      return column.cardIds
        .map(id => cards[id])
        .filter(card => {
          if (!card) return false
          const cardRespId = card.content.responsible?.uuid ?? null
          return cardRespId === responsibleUuid
        })
    },
    [columns, cards]
  )

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const { active } = event
      const target: CardData | undefined = cards[active.id]
      setActiveContent(target?.content)
    },
    [cards]
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      const activeId = String(active.id)
      const overId = over ? String(over.id) : null

      setActiveContent(undefined)

      if (!overId) return

      // スイムレーン制約をチェック（スイムレーン有効時のみ）
      if (isSwimlaneEnabled) {
        const activeRespId = getResponsibleIdFromCard(activeId)
        const overRespId = getResponsibleIdFromOverId(overId)

        // スイムレーン間のドロップをブロック
        if (activeRespId !== overRespId) {
          return
        }
      }

      const { column: activeColumn } = findColumnFromId(activeId)
      const { column: overColumn, status: overStatus } =
        findColumnFromId(overId)

      if (!activeColumn || !overColumn || !overStatus) {
        return
      }

      // 同じカラム内での並び替え
      if (activeColumn === overColumn) {
        const activeIndex = activeColumn.cardIds.findIndex(i => i === activeId)
        const overIndex = overColumn.cardIds.findIndex(i => i === overId)
        if (
          activeIndex !== overIndex &&
          activeIndex !== -1 &&
          overIndex !== -1
        ) {
          setColumns(prev => {
            return prev.map(column => {
              if (column.id === activeColumn.id) {
                return {
                  ...column,
                  cardIds: arrayMove(column.cardIds, activeIndex, overIndex),
                }
              } else {
                return column
              }
            })
          })
        }
      }

      // ステータスが変更された場合は更新
      if (overlayActiveContent?.status !== overStatus) {
        handleChangeCardContent(activeId, 'status', overStatus)
      }
    },
    [
      findColumnFromId,
      overlayActiveContent,
      getResponsibleIdFromCard,
      getResponsibleIdFromOverId,
      handleChangeCardContent,
      setColumns,
      isSwimlaneEnabled,
    ]
  )

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over, delta } = event
      const activeId = String(active.id)
      const overId = over ? String(over.id) : null

      if (!overId) return

      // スイムレーン制約をチェック（スイムレーン有効時のみ）
      if (isSwimlaneEnabled) {
        const activeRespId = getResponsibleIdFromCard(activeId)
        const overRespId = getResponsibleIdFromOverId(overId)

        // スイムレーン間のドラッグをブロック
        if (activeRespId !== overRespId) {
          return
        }
      }

      const { column: activeColumn } = findColumnFromId(activeId)
      const { column: overColumn } = findColumnFromId(overId)

      if (!activeColumn || !overColumn || activeColumn === overColumn) {
        return
      }

      // カラム間でカードを一時的に入れ替えて移動
      setColumns(prevState => {
        const activeCol = prevState.find(c => c.id === activeColumn.id)
        const overCol = prevState.find(c => c.id === overColumn.id)
        if (!activeCol || !overCol) return prevState

        const activeItems = activeCol.cardIds
        const overItems = overCol.cardIds
        const overIndex = overItems
          .filter(i => !!i)
          .findIndex(i => i === overId)
        const newIndex = () => {
          const putOnBelowLastItem =
            overIndex === overItems.length - 1 && delta.y > 0
          const modifier = putOnBelowLastItem ? 1 : 0
          return overIndex >= 0 ? overIndex + modifier : overItems.length
        }
        return prevState.map(c => {
          if (c.id === activeColumn.id) {
            return {
              ...c,
              cardIds: activeItems.filter(i => i !== activeId),
            }
          } else if (c.id === overColumn.id) {
            return {
              ...c,
              cardIds: [
                ...overItems.slice(0, newIndex()),
                activeId,
                ...overItems.slice(newIndex()),
              ],
            }
          } else {
            return c
          }
        })
      })
    },
    [
      findColumnFromId,
      getResponsibleIdFromCard,
      getResponsibleIdFromOverId,
      setColumns,
      isSwimlaneEnabled,
    ]
  )

  return (
    <DndComponent
      delay={35}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      primaryButtonOnly={true}
    >
      <DndArea headerBodyGap={isSwimlaneEnabled ? 4 : 8}>
        {/* ステータスヘッダー行 - 上部固定 */}
        <StatusHeaderRow>
          {columns.map(column => (
            <KanbanColumn
              key={`header-${column.id}`}
              backgroundColor={column.backgroundColor}
              isCollapsed={columnCollapseState[column.id] ?? false}
              onToggleCollapse={() => toggleColumnCollapse(column.id)}
            >
              <StatusColumnHeader
                title={column.title}
                cumulationData={filteredCumulationByColumn[column.id]}
                backgroundColor={column.backgroundColor}
              />
            </KanbanColumn>
          ))}
        </StatusHeaderRow>

        {/* スイムレーン行 */}
        <SwimlanesContainer ref={setScrollContainerRef}>
          {isSwimlaneEnabled ? (
            // スイムレーン有効時: 担当者ごとにグループ化
            swimlanes
              .filter(
                swimlane => (swimlaneCumulation[swimlane.id]?.count ?? 0) > 0
              )
              .map(swimlane => {
                // ドラッグ中で、かつ現在のスイムレーンがドラッグ元と異なる場合は薄くする
                const isDimmed =
                  isDragging &&
                  (swimlane.responsibleUuid ?? null) !== draggingResponsibleId
                return (
                  <SwimlaneRow
                    key={swimlane.id}
                    ref={ref => registerSwimlaneRowRef(swimlane.id, ref)}
                    swimlane={swimlane}
                    cumulation={
                      swimlaneCumulation[swimlane.id] ?? {
                        count: 0,
                        estimatedHour: 0,
                        actualHour: 0,
                      }
                    }
                    statusCumulation={
                      swimlaneStatusCumulation[swimlane.id] ?? {}
                    }
                    columns={columns}
                    columnCollapseState={columnCollapseState}
                    isCollapsed={swimlaneCollapseState[swimlane.id] ?? false}
                    onToggleCollapse={() =>
                      onToggleSwimlaneCollapse(swimlane.id)
                    }
                    isDimmed={isDimmed}
                  >
                    {columns.map(column => {
                      const isColumnCollapsed =
                        columnCollapseState[column.id] ?? false
                      return (
                        <SwimlaneCell
                          key={`${swimlane.id}-${column.id}`}
                          isCollapsed={isColumnCollapsed}
                        >
                          {!isColumnCollapsed && (
                            <DroppableWrapper
                              id={createDroppableId(
                                swimlane.responsibleUuid,
                                column.id
                              )}
                              cards={getCardsForCell(
                                swimlane.responsibleUuid,
                                column.id
                              )}
                              style={{
                                minHeight: '60px',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 4px',
                              }}
                            >
                              {getCardsForCell(
                                swimlane.responsibleUuid,
                                column.id
                              )
                                .filter(filter)
                                .map(card => (
                                  <Card
                                    key={`card-${card.id}`}
                                    id={card.id}
                                    showDragHandle={false}
                                  >
                                    <WbsStatusCardContent
                                      content={card.content}
                                      onChange={handleChangeCardContent}
                                      onCloseWbsItemDetail={code =>
                                        refreshSingle(code, searchCondition)
                                      }
                                    />
                                  </Card>
                                ))}
                            </DroppableWrapper>
                          )}
                        </SwimlaneCell>
                      )
                    })}
                  </SwimlaneRow>
                )
              })
          ) : (
            // スイムレーン無効時: フラットにカード表示
            <FlatCardsRow>
              {columns.map(column => {
                const isColumnCollapsed =
                  columnCollapseState[column.id] ?? false
                return (
                  <FlatColumnCell
                    key={column.id}
                    isCollapsed={isColumnCollapsed}
                  >
                    {!isColumnCollapsed && (
                      <DroppableWrapper
                        id={column.id}
                        cards={column.cardIds
                          .map(id => cards[id])
                          .filter(Boolean)}
                        style={{
                          minHeight: '60px',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 4px',
                        }}
                      >
                        {column.cardIds
                          .map(id => cards[id])
                          .filter(card => card && filter(card))
                          .map(card => (
                            <Card
                              key={`card-${card.id}`}
                              id={card.id}
                              showDragHandle={false}
                            >
                              <WbsStatusCardContent
                                content={card.content}
                                onChange={handleChangeCardContent}
                                onCloseWbsItemDetail={code =>
                                  refreshSingle(code, searchCondition)
                                }
                              />
                            </Card>
                          ))}
                      </DroppableWrapper>
                    )}
                  </FlatColumnCell>
                )
              })}
            </FlatCardsRow>
          )}
        </SwimlanesContainer>
      </DndArea>
      <DragOverlay>
        {overlayActiveContent ? (
          <Card
            id={`${overlayActiveContent.uuid}`}
            cardStyle={{ transform: 'scale(1.05)' }}
            isDragOverlay={true}
            showDragHandle={false}
          >
            <WbsStatusCardContent content={overlayActiveContent} />
          </Card>
        ) : null}
      </DragOverlay>
    </DndComponent>
  )
}

const DndArea = styled('div')<{ headerBodyGap: number }>(props => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  padding: '8px 8px 0 8px',
  gap: `${props.headerBodyGap}px`,
  overflow: 'auto',
  flex: 1,
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
}))

const StatusHeaderRow = styled('div')({
  display: 'flex',
  flexWrap: 'nowrap',
  gap: '8px',
  flexShrink: 0,
})

const SwimlanesContainer = styled('div')({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  flex: 1,
  overflow: 'auto',
  paddingBottom: '8px',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
})

const SwimlaneCell = styled('div')<{ isCollapsed: boolean }>(props => ({
  flex: props.isCollapsed ? 'none' : 1,
  minWidth: props.isCollapsed ? `${COLLAPSED_COLUMN_WIDTH}px` : '260px',
  backgroundColor: colorPalette.monotone[0],
  borderRadius: '8px',
  border: `1px solid ${colorPalette.monotone[2]}`,
}))

const FlatCardsRow = styled('div')({
  display: 'flex',
  flexWrap: 'nowrap',
  gap: '8px',
  flex: 1,
})

const FlatColumnCell = styled('div')<{ isCollapsed: boolean }>(props => ({
  flex: props.isCollapsed ? 'none' : 1,
  minWidth: props.isCollapsed ? `${COLLAPSED_COLUMN_WIDTH}px` : '260px',
  backgroundColor: colorPalette.monotone[0],
  borderRadius: '8px',
  border: `1px solid ${colorPalette.monotone[2]}`,
}))
